import React, { useEffect, useMemo, useState, useRef } from "react"
import { Route, withRouter } from "react-router-dom"
import { CommentProvider } from "@components/comments"
import { TeaserStatisticsContainer } from "@components/TeaserStatistics/TeaserStatistics"
import { TeaserStatisticsProvider } from "@components/TeaserStatistics/TeaserStatisticsProvider"
import { AdminContextProvider, DnDContext } from "@components/ui-builder/index"
import {
  AdminFeedProvider,
  Library,
  TeaserCollection,
  TeaserDragSource,
  TeaserEdit,
} from "./admin"
import { ConnectedFeedAdminToolbar } from "./admin/FeedAdminToolbar"
import Filter, { filterItems } from "./filter"
import Item from "./item"
import { DealRoute, DetailRoute } from "./routes"
import Sort, { sortItems } from "./sort"
import additionalTeaserStats from "./teaser/additionalTeaserStats"

const StatsWithInfos = ({ teaserId, item, ...props }) => (
  <TeaserStatisticsContainer
    {...props}
    teaserId={teaserId}
    stats={additionalTeaserStats(item)}
  />
)

// This makes sure we only scroll to the anchor on the initial page load
let scrolled = 0
const scrollToAnchor = (pathname, navigate) => () => {
  if (!scrolled++ && pathname === "/") {
    const ank = new URLSearchParams(location.search).get("ank")
    if (ank) {
      navigate(parseInt(ank) ? "teaser_" + ank : ank)
    }
  }
}
const __temporaryGroupByOverviewElement = items =>
  items.reduce(
    (agg, item) => {
      if (!agg.current) {
        agg.current = agg.root
      }
      if (item && item.view_widget === "overview" && item.toggle_wrapper) {
        const current = []
        agg.root = [...agg.root, current]
        agg.current = current
      }
      agg.current.push(item)

      return agg
    },
    {
      root: [],
      current: null,
    }
  ).root

const renderItem = item => (
  <StatsWithInfos key={item.id} teaserId={item.teaser_id} item={item}>
    <Item {...item} />
  </StatsWithInfos>
)

const List = ({ items, sort, filter, options }) => {
  // Sort and filter items
  const result = useMemo(
    () =>
      __temporaryGroupByOverviewElement(
        sortItems(filterItems(items, filter), sort, filter)
      ),
    [items, sort, filter]
  )

  const { background_section, background_nosection } = options
  const classSection = background_section
    ? `content-feed-background-${background_section}`
    : ""
  const classNoSection = background_nosection
    ? `content-feed-background-${background_nosection}`
    : ""

  return result.map(group =>
    Array.isArray(group) ? (
      <div key={group[0].id} className={`content-feed-section ${classSection}`}>
        {group.map(renderItem)}
      </div>
    ) : (
      <div
        key={group.id}
        className={`content-feed-no-section ${classNoSection}`}
      >
        {renderItem(group)}
      </div>
    )
  )
}

const Page = ({
  items,
  feed_id,
  feed_url,
  view_options,
  location = {},
  history = {},
}) => {
  useEffect(scrollToAnchor(location.pathname, history.replace), [])

  // Sort
  const [sort, setSort] = useState(view_options.sort)

  // Filter
  // TODO: Uses search, so don't access it on the 1st render and force a 2nd
  // one on the client to not break SSR
  const params = new URLSearchParams(location.search)
  const filter =
    useRef(0).current++ && params.has("filter")
      ? "content-feed-lane-" + params.get("filter")
      : view_options.filterSelected
  const setFilter = v => {
    if (v !== filter) {
      if (v.startsWith("*")) {
        params.delete("filter")
      } else {
        params.set("filter", v.replace(/^content-feed-lane-/, ""))
      }
      history.push(location.pathname + "?" + params.toString())
    }
  }

  useEffect(() => {
    params.has("filter") && setFilter(params.get("filter"))
  }, [])

  return (
    <AdminContextProvider>
      <TeaserStatisticsProvider>
        <CommentProvider contextType="ContentFeed" contextId={feed_id}>
          <AdminFeedProvider {...{ items, feed_id }}>
            <DnDContext>
              <ConnectedFeedAdminToolbar />
              {view_options.sort || view_options.filterOptions ? (
                <div className="content-feed-action-bar">
                  <Sort
                    sort={sort}
                    defaultSort={view_options.sort}
                    onSort={setSort}
                  />
                  <Filter
                    selected={filter}
                    onSelect={setFilter}
                    options={view_options.filterOptions}
                  />
                </div>
              ) : null}
              <div className="content-feed-list">
                <TeaserCollection
                  defaultRender={() => (
                    <List
                      items={items}
                      sort={sort}
                      filter={filter}
                      options={view_options}
                    />
                  )}
                  data={items}
                  renderItem={item => (
                    <StatsWithInfos
                      key={item.id}
                      teaserId={item.id}
                      item={item}
                    >
                      <TeaserDragSource item={item}>
                        <TeaserEdit item={item} showEdit={!item.is_placeholder}>
                          <Item {...item} item={item} />
                        </TeaserEdit>
                      </TeaserDragSource>
                    </StatsWithInfos>
                  )}
                />
              </div>
              <DetailRoute items={items} />
              <DealRoute items={items} />

              <Library feedUrl={feed_url} />
            </DnDContext>
          </AdminFeedProvider>
        </CommentProvider>
      </TeaserStatisticsProvider>
    </AdminContextProvider>
  )
}

export default withRouter(Page)
